import React, { useEffect } from "react";
import { ImageBackground, ScrollView } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  MainText,
  MainTitle,
  MySafeAreaView,
  SkillLabel,
} from "app/components/common";
import i18n from "@microdoing/shared/i18n";
import { changeNavField } from "@microdoing/shared/services/nav/actions";
import { widthPercentageToDP as wp } from "react-native-responsive-screen";
import selectLanguage from "../../utils/selectLanguage";
import SocialSheet from "app/scenes/CurrentTrackScreen/DailyFocusScreen/SelectAction/SocialSheet";
import isActionEmpty from "@microdoing/shared/utils/isActionEmpty";
import { getFeedbackSelected } from "@microdoing/shared/services/historical/selector";
import theme from "app/Theme/Theme";
import { getLanguage } from "@microdoing/shared/services/profileSettings/selectors";
import selectActionLanguage from "app/utils/selectActionLanguage";
import WebDesktopView from "app/components/common/Web/WebDesktopView";
import { breadCrumbsActionAfestDetails } from "app/navigation/breadcrumb/BreadcrumbPaths";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import WebAnalysisBoxing from "app/components/common/Web/WebAnalysisBoxing";
import CommentList from "../FeedbackScreen/ReflexiveAnalysis/Action/CommentList";
import moment from "moment";
import ActionText from "../ActionScreen/ActionText";
import normalize from "app/utils/normalize";
import HeaderActionScreen from "../ActionScreen/HeaderActionScreen";
import ReviewChat from "./ReviewChat";
import {
  getHasCampaignReviews,
  getStepSelected,
} from "@microdoing/shared/services/userCampaign/selectors";
import AnalysisBox from "./AnalysisBox";
import { getStatusColor } from "app/utils/getStatusReview";
import ReviewStatusChip from "./ReviewStatusChip";

export const MARGIN_HORIZONTAL = wp("8%");

const firstImage = require("../../../assets/images/BackgroundGradient.png");

interface IProps {}

const ActionHistorical = ({}: IProps) => {
  const feedbackSelected = useSelector(getFeedbackSelected); // ? all information about the feedback but not the steps
  const step = useSelector(getStepSelected);

  const hasReview = useSelector(getHasCampaignReviews);
  const language = useSelector(getLanguage);

  const actionTitle = step?.title;
  const category = step?.category;
  const { totalDone, skills = [] } = step;
  const { title, how, why } = step?.content || {};
  const skill = skills?.[0] || "";
  const doneDate = feedbackSelected.createdAt;
  const review = feedbackSelected.review;
  const contentTitle = selectActionLanguage({ text: title, language });
  const contentHow = selectActionLanguage({ text: how, language });
  const contentWhy = selectActionLanguage({ text: why, language });
  const { rating, status = "new" } = feedbackSelected || {};

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      changeNavField({
        safeAreaViewBackgroundColor: theme.colors["neutral.white"],
      }),
    );
  }, []);

  return (
    <WebDesktopView breadcrumbsPath={breadCrumbsActionAfestDetails}>
      <WebAnalysisBoxing
        leftComponent={
          <MySafeAreaView
            paddingHorizontal={isWebDesktop ? "sl" : "sm"}
            paddingVertical={isWebDesktop ? "sl" : "xxs"}
            borderRadius={8}
            flex={1}
            backgroundColor="neutral.white"
          >
            <HeaderActionScreen
              title={
                selectLanguage({ text: actionTitle, language }) || `Action`
              }
            />

            <ScrollView
              style={{
                width: "100%",
              }}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={{
                paddingBottom: 100,
              }}
            >
              <Box flexDirection="row" alignItems="center" marginTop="s">
                {!!doneDate && (
                  <MainText
                    variant="l3-medium"
                    marginRight="xs"
                    color="neutral.600"
                  >
                    {moment(doneDate).format("LL")}
                  </MainText>
                )}
                {!!skill && (
                  <SkillLabel
                    text={selectLanguage({ text: skill?.label, language })}
                  />
                )}
              </Box>
              {!!status && hasReview && (
                <Box
                  width="100%"
                  marginTop="sm"
                  height={normalize(18)}
                  alignItems="center"
                  flexDirection="row"
                >
                  <MainText variant="l3-medium" marginRight="xs">
                    {i18n.t("Status")}
                  </MainText>
                  <ReviewStatusChip status={status} />
                </Box>
              )}
              <Box>
                <Box marginBottom="l" marginTop="sm">
                  {hasReview && (
                    <ReviewChat
                      review={review}
                      stepFeedbackId={feedbackSelected._id}
                    />
                  )}
                  <Box marginTop="sm">
                    <AnalysisBox />
                  </Box>
                </Box>
                <MainTitle variant="l5-bold" marginBottom="xs">
                  {i18n.t("challenge")}
                </MainTitle>
                <ActionText text={contentTitle ? contentTitle : " "} />
                {!isActionEmpty(contentWhy) && (
                  <>
                    <MainTitle
                      marginTop="sm"
                      marginBottom={0}
                      variant="l4-bold"
                    >
                      {i18n.t("why")}
                    </MainTitle>
                    <ActionText text={contentWhy} />
                  </>
                )}
                {!isActionEmpty(contentHow) && (
                  <>
                    <MainTitle
                      variant="l4-bold"
                      marginTop="sm"
                      marginBottom="xxs"
                    >
                      {i18n.t("concrete-example")}
                    </MainTitle>
                    <ActionText text={contentHow} />
                  </>
                )}
              </Box>
            </ScrollView>
            {!isWebDesktop && (
              <SocialSheet
                contentTitle={contentTitle}
                isSmall={true}
                rating={rating}
                totalDone={totalDone}
                numberOfComments={step?.numberOfComments || 0}
                users={step?.users || []}
              />
            )}
          </MySafeAreaView>
        }
        rightComponent={
          <Box
            flex={1}
            paddingHorizontal={{ web: "l", smallWeb: "sl" }}
            paddingVertical={{ web: "sl" }}
            overflow="scroll"
          >
            <CommentList />
          </Box>
        }
      />
    </WebDesktopView>
  );
};

export default ActionHistorical;
